import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faSearch,
	faChevronLeft,
	faChevronRight,
	faAngleDoubleLeft,
	faAngleDoubleRight,
} from "@fortawesome/free-solid-svg-icons"
import { fetchEntites } from "../../../store/actions/entiteActions"
import { getBaseEntite } from "../../../utilities/defaultObjects"
import {
	ENTITE_TYPE_ASSURANCE,
	ENTITE_TYPE_POLICE,
	ENTITE_TYPE_SOUS_TRAITANT,
	ENTITE_TYPE_GARAGE,
	ENTITE_TYPE_BUREAU_EXPERT,
	ENTITE_TYPE_ASSISTANCE,
	ENTITE_TYPES,
} from "../entite/Entite"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { tableStyles } from "../../styles/tableStyles"
import CustomModal from "../../basics/interface/CustomModal"
import Loader from "../../basics/Loader"
import EntiteFicheV2 from "./EntiteFicheV2"

const PAGE_SIZE = 20

// Types non-client / non-master gérés ici.
const TYPES_GERES = [
	ENTITE_TYPE_ASSURANCE,
	ENTITE_TYPE_POLICE,
	ENTITE_TYPE_SOUS_TRAITANT,
	ENTITE_TYPE_GARAGE,
	ENTITE_TYPE_BUREAU_EXPERT,
	ENTITE_TYPE_ASSISTANCE,
]
const TYPE_OPTIONS = ENTITE_TYPES.filter((e) =>
	TYPES_GERES.includes(e[0]),
).sort((a, b) => a[1].localeCompare(b[1], "fr"))
const typeLabel = (t) => {
	const f = ENTITE_TYPES.find((e) => e[0] === t)
	return f ? f[1] : "—"
}

const styles = {
	toolbar: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 16,
		marginBottom: 18,
		flexWrap: "wrap",
	},
	left: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
	typeSelect: {
		padding: "9px 12px",
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
		fontSize: 13,
		color: COLORS.text,
		backgroundColor: COLORS.surface,
		fontFamily: "inherit",
		outline: "none",
	},
	searchWrap: { position: "relative", width: 240, maxWidth: "60vw" },
	searchIcon: {
		position: "absolute",
		left: 13,
		top: "50%",
		transform: "translateY(-50%)",
		color: COLORS.textLight,
		fontSize: 13,
		pointerEvents: "none",
	},
	searchInput: {
		width: "100%",
		padding: "9px 12px 9px 36px",
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
		fontSize: 13,
		color: COLORS.text,
		backgroundColor: COLORS.surface,
		outline: "none",
		fontFamily: "inherit",
		boxSizing: "border-box",
	},
	empty: {
		...tableStyles.empty,
		padding: "48px 16px",
		color: COLORS.textLight,
		fontSize: 14,
	},
	pagination: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
		marginTop: 16,
	},
	pageBtn: (disabled) => ({
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		width: 34,
		height: 34,
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: disabled ? COLORS.textLight : COLORS.text,
		cursor: disabled ? "default" : "pointer",
		opacity: disabled ? 0.45 : 1,
		fontSize: 13,
	}),
	pageInfo: {
		fontSize: 13,
		color: COLORS.textMuted,
		minWidth: 92,
		textAlign: "center",
	},
	typeChip: {
		display: "inline-flex",
		alignItems: "center",
		padding: "2px 9px",
		borderRadius: 99,
		fontSize: 11,
		fontWeight: 500,
		color: COLORS.accent,
		backgroundColor: COLORS.accentLight,
	},
	newModalRow: {
		display: "flex",
		gap: 12,
		justifyContent: "center",
		flexWrap: "wrap",
	},
	newChoice: {
		minWidth: 150,
		padding: "16px",
		borderRadius: 12,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		fontSize: 14,
		fontWeight: 600,
		fontFamily: "inherit",
		cursor: "pointer",
		transition: "border-color 0.15s",
	},
	back: {
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		fontSize: 13,
		fontWeight: 500,
		color: COLORS.textMuted,
		background: "none",
		border: "none",
		cursor: "pointer",
		padding: 0,
		marginBottom: 16,
		fontFamily: "inherit",
	},
}

const EntiteRow = ({ ent, index, onOpen }) => {
	const [hover, setHover] = useState(false)
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	return (
		<tr
			onDoubleClick={() => onOpen(ent)}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			<td style={tableStyles.td}>
				<strong style={{ fontWeight: 600 }}>{ent.label || "—"}</strong>
			</td>
			<td style={tableStyles.td}>
				<span style={styles.typeChip}>{typeLabel(ent.type)}</span>
			</td>
			<td style={tableStyles.td}>{ent.telephone || "—"}</td>
			<td style={{ ...tableStyles.td, color: COLORS.textMuted }}>
				{ent.email || "—"}
			</td>
		</tr>
	)
}

// Onglet "Entité" de la Configuration : table des entités non-client/non-master
// + fiche inline (master/détail). La fiche réelle arrive à l'étape 2.
const EntitesV2 = () => {
	const dispatch = useDispatch()
	const { entites, entitesPagination, fetchEntites: fetchState } =
		useSelector((s) => s.entiteInfo)

	const [search, setSearch] = useState("")
	const [typeFilter, setTypeFilter] = useState("all")
	const [newOpen, setNewOpen] = useState(false)
	const [editing, setEditing] = useState(null) // entite | null

	const buildParams = (limit = PAGE_SIZE, offset = 0) => {
		const params = { offset, limit }
		if (typeFilter === "all") params.type__in = TYPES_GERES.join(",")
		else params.type = typeFilter
		if (search) params.label__icontains = search
		return params
	}

	useEffect(() => {
		if (editing) return
		if (search.length > 3 || search.length === 0) {
			dispatch(fetchEntites(buildParams(PAGE_SIZE, 0)))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search, typeFilter, editing])

	const goTo = (node) => {
		if (node) dispatch(fetchEntites(buildParams(node.limit, node.offset)))
	}
	const createEntite = (type) => {
		setNewOpen(false)
		setEditing({ ...getBaseEntite(), type })
	}

	const pag = entitesPagination || {}
	const totalPages = pag.last
		? Math.floor(pag.last.offset / pag.last.limit) + 1
		: pag.current || 1
	const loading = fetchState.loading

	// ===== Fiche inline (master/détail) =====
	if (editing) {
		return (
			<EntiteFicheV2
				entite={editing}
				onClose={() => setEditing(null)}
			/>
		)
	}

	// ===== Table =====
	return (
		<div>
			<div style={styles.toolbar}>
				<div style={styles.left}>
					<select
						style={styles.typeSelect}
						value={typeFilter}
						onChange={(e) =>
							setTypeFilter(
								e.target.value === "all"
									? "all"
									: parseInt(e.target.value),
							)
						}
					>
						<option value="all">Tous les types</option>
						{TYPE_OPTIONS.map((t) => (
							<option key={t[0]} value={t[0]}>
								{t[1]}
							</option>
						))}
					</select>
					<div style={styles.searchWrap}>
						<FontAwesomeIcon
							icon={faSearch}
							style={styles.searchIcon}
						/>
						<input
							style={styles.searchInput}
							placeholder="Rechercher…"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							onFocus={(e) =>
								(e.target.style.borderColor = COLORS.borderStrong)
							}
							onBlur={(e) =>
								(e.target.style.borderColor = COLORS.border)
							}
						/>
					</div>
				</div>
				<button style={gs.btnPrimary} onClick={() => setNewOpen(true)}>
					+ Nouvelle entité
				</button>
			</div>

			<div style={tableStyles.container}>
				{loading ? (
					<Loader variant="ring" message="Chargement…" />
				) : entites.length === 0 ? (
					<div style={styles.empty}>Aucune entité trouvée.</div>
				) : (
					<table style={tableStyles.table}>
						<thead>
							<tr>
								<th style={tableStyles.th}>Nom</th>
								<th style={tableStyles.th}>Type</th>
								<th style={tableStyles.th}>Téléphone</th>
								<th style={tableStyles.th}>E-mail</th>
							</tr>
						</thead>
						<tbody>
							{entites.map((ent, i) => (
								<EntiteRow
									key={ent.id}
									ent={ent}
									index={i}
									onOpen={setEditing}
								/>
							))}
						</tbody>
					</table>
				)}
			</div>

			{!loading && entites.length > 0 && (
				<div style={styles.pagination}>
					<button
						style={styles.pageBtn(!pag.first)}
						disabled={!pag.first}
						onClick={() => goTo(pag.first)}
						title="Première page"
					>
						<FontAwesomeIcon icon={faAngleDoubleLeft} />
					</button>
					<button
						style={styles.pageBtn(!pag.previous)}
						disabled={!pag.previous}
						onClick={() => goTo(pag.previous)}
						title="Page précédente"
					>
						<FontAwesomeIcon icon={faChevronLeft} />
					</button>
					<span style={styles.pageInfo}>
						{`Page ${pag.current || 1} / ${totalPages}`}
					</span>
					<button
						style={styles.pageBtn(!pag.next)}
						disabled={!pag.next}
						onClick={() => goTo(pag.next)}
						title="Page suivante"
					>
						<FontAwesomeIcon icon={faChevronRight} />
					</button>
					<button
						style={styles.pageBtn(!pag.last)}
						disabled={!pag.last}
						onClick={() => goTo(pag.last)}
						title="Dernière page"
					>
						<FontAwesomeIcon icon={faAngleDoubleRight} />
					</button>
				</div>
			)}

			{newOpen && (
				<CustomModal
					title="Nouvelle entité"
					onClose={() => setNewOpen(false)}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: () => setNewOpen(false),
						},
					]}
				>
					<p
						style={{
							margin: "0 0 18px",
							fontSize: 13,
							color: COLORS.textMuted,
							textAlign: "center",
						}}
					>
						Quel type d'entité créer ?
					</p>
					<div style={styles.newModalRow}>
						{TYPE_OPTIONS.map((t) => (
							<button
								key={t[0]}
								style={styles.newChoice}
								onClick={() => createEntite(t[0])}
								onMouseEnter={(e) =>
									(e.currentTarget.style.borderColor =
										COLORS.accent)
								}
								onMouseLeave={(e) =>
									(e.currentTarget.style.borderColor =
										COLORS.border)
								}
							>
								{t[1]}
							</button>
						))}
					</div>
				</CustomModal>
			)}
		</div>
	)
}

export default EntitesV2
